import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  College,
  Employer,
  convertToMarkdown,
  GeneratedContent,
  handleAskLubavApiCall,
  ResourcePages,
  ResourcePagesType,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LUBAV_API_URL } from '@backrs_ui/grow-ui/src/settings';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { MakeItPersonalBadge, setActiveMakeItPersonalBadge } from './makeItPersonal';
import { makeItPersonalApi } from './makeItPersonal.api';

const pageTypeToMinorSource = (
  pageType: ResourcePagesType,
  college?: College,
): string | undefined => {
  return pageType === ResourcePages.COLLEGE ? college?.school_name : undefined;
};

export const sendMakeItPersonalAskLubavPrompt = createAsyncThunk(
  'makeItPersonal/sendMakeItPersonalAskLubavPrompt',
  async (
    params: {
      page: ResourcePagesType;
      askLubavBadge: MakeItPersonalBadge;
      college?: College;
      employer?: Employer;
      user_id: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    const accessToken = getBackrsToken();

    if (!accessToken) {
      return rejectWithValue('No access token found');
    }

    if (!LUBAV_API_URL) {
      return rejectWithValue('No LUBAV API URL found');
    }

    try {
      const url_key = params.askLubavBadge.endpoint;

      const payload = {
        college_id: params.college?.id,
        employer_id: params.employer?.id,
        user_id: params.user_id,
      };

      const chunks: string[] = [];

      const response = await handleAskLubavApiCall({
        url_key: url_key,
        apiUrl: LUBAV_API_URL,
        inputData: payload,
        userId: 1,
        getToken: getBackrsToken,
        askLubavResponseAccumulator: (chunk) => {
          // add the new chunk to array
          chunks.push(chunk);

          dispatch(
            setActiveMakeItPersonalBadge({
              ...params.askLubavBadge,
              response: chunks.join(''),
              isLoading: true,
            }),
          );
        },
      });

      const finalResponse = response?.response || chunks.join('');

      const minorSource = pageTypeToMinorSource(params.page, params.college);
      // once we've finished streaming, save the response
      const generatedContent = await dispatch(
        makeItPersonalApi.endpoints.createGeneratedContent.initiate({
          title: params.askLubavBadge.title,
          content: convertToMarkdown(JSON.parse(finalResponse)),
          user_id: params.user_id,
          major_source: params.page,
          ...(minorSource !== undefined && { minor_source: minorSource }),
          summary: JSON.parse(finalResponse).summary,
        }),
      ).unwrap();

      // we also need to update the badge with the generated content as written
      dispatch(
        setActiveMakeItPersonalBadge({
          ...params.askLubavBadge,
          response: finalResponse,
          isLoading: false,
          generatedContent: generatedContent as GeneratedContent,
        }),
      );

      // return the final response (which shouldn't be any different from the response action)
      return finalResponse;
    } catch (error) {
      console.error('Failed to call Lubav college prompt with error: ', error);

      return appStrings.modules.chatBox.overloadErr;
    }
  },
);
